/* eslint-disable prettier/prettier */
import { ENV } from '@environment';
import * as Sentry from '@sentry/react';
import { travel, axiosConfig } from 'api';
import axios, { AxiosResponse } from 'axios';
import moment from 'moment';
import { Dispatch } from 'redux';
import { RootState } from 'store';

let cancel;
const CancelToken = axios.CancelToken;

import {
  flightsSearchRequest,
  flightsSearchSuccessful,
  flightsSearchError,
  flightResultsRequest,
  flightResultsSuccessful,
  flightResultsBySectorSuccessful,
  flightResultsError,
  flightsClear,
  flightsTabFilterSuccessful,
  flightsExchangeRequest,
  flightsExchangeSuccessful,
  flightsExchangeError,
  flightsExchangeClear,
  changeResultBySectorPagination,
  selectFlightBySectorSuccessful,
  flightResultsBySectorSuccessfulFilter,
  setConfirmationSuccessful,
  setConfirmationError
} from 'action-creators/flights';
import { TimeOption } from 'components/FlightsSearch/components/FlightTimePicker/models';
import { AdminUser } from 'models/adminUser';
import { Airport } from 'models/airport';
import { GetAllBillingCompanyResponse } from 'models/billingCompany';
import { FlightExchangeResponse } from 'models/flightExchange';
import { FlightFilter, FlightFilterResponse } from 'models/flightFilter';
import {
  FlightSearchRequest,
  FlightSearchResponse,
  FlightResultRequest,
  FlightResultResponse,
  Trip,
  FlightResultRequestOrderCriteriaEnum,
  ExchangeTrip,
  FlightExchangeRequest,
  FlightStretch,
  FlightResultRequestBySector,
} from 'models/flightSearch';
import { FlightSolicitationRequest, AirBook, Traveler } from 'models/flightSolicitation';
import { FlightValidateResponse } from 'models/flightValidate';
import { FlightValidationResponse, FlightValidationRequest, FlightValidationResponseBySector, TotalPrice } from 'models/flightValidation';
import { MultidestinationTrip } from 'models/multiDestinationTrip';
import { Price } from 'models/price';
import { Response } from 'models/response';
import { Sector } from 'models/sector';
import { TravelRequested } from 'models/travelRequested';
import { amplitudelogEvent, TravelErrorEvent } from 'services/amplitudeService';
import { TravelArrangerUsersAllowed } from 'services/userService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { daysCount } from 'utils/generic';
import { convertTime } from 'utils/time';

import {
  flightsExchangeResultsError,
  flightsExchangeResultsRequest,
  flightsExchangeResultsSuccessful,
} from './../action-creators/flights';
import { ErrorPayload } from './../models/error';
import { updateAccount } from './user';
import { UpsellBySectorRequest } from 'models/upsell';
import { setTravelItineraryId } from 'action-creators/travel';

type SearchFlightsArguments = {
  baggageIncluded: boolean;
  departurePoint: Airport;
  arrivalPoint: Airport;
  departureDate: Date;
  travelers?: TravelArrangerUsersAllowed[];
  cabin: string;
  returnDate?: Date;
  newSessionToken?: string;
  departureTime?: TimeOption;
  returnTime?: TimeOption;
  directFlight: boolean;
  branchCompany?: GetAllBillingCompanyResponse;
  multiDestinationTrips?: MultidestinationTrip[];
  flightStretch: FlightStretch;
};

/* logica para flujo de  search by sector */
export const searchFlightsBySector =
  ({
    baggageIncluded,
    departurePoint,
    arrivalPoint,
    departureDate,
    travelers,
    cabin,
    returnDate,
    newSessionToken,
    departureTime,
    returnTime,
    directFlight,
    branchCompany,
    multiDestinationTrips,
    flightStretch,
  }: SearchFlightsArguments) =>
  async (dispatch: Dispatch, getState: () => RootState) =>
    new Promise(async (resolve, reject) => {
      dispatch(
        flightsSearchRequest({
          baggageIncluded,
          departurePoint,
          arrivalPoint,
          departureDate,
          cabin,
          returnDate,
          travelers,
          departureTime,
          returnTime,
          directFlight,
          branchCompany,
          multiDestinationTrips,
          flightStretch,
        }),
      );

      const { accountResult } = getState().user;
      if (!accountResult) return;

      const { sessionToken, id: loggedUserId, userGroupId, countryCurrency } = accountResult;
      
      try {
        let trips: Trip[] = [];
        if (flightStretch !== FlightStretch.MULTIDESTINATION) {
          const departureDateTime = departureTime
            ? `${moment(departureDate).format('YYYY-MM-DD')}T${convertTime(departureTime)}`
            : `${moment(departureDate).format('YYYY-MM-DD')}T00:00:01Z`;
          trips = [
            new Trip(departurePoint.code, arrivalPoint.code, departureDateTime, cabin, baggageIncluded, directFlight),
          ];

          if (returnDate) {
            const returnDateTime = returnTime
              ? `${moment(returnDate).format('YYYY-MM-DD')}T${convertTime(returnTime)}`
              : `${moment(returnDate).format('YYYY-MM-DD')}T00:00:02Z`;
            trips.push(
              new Trip(arrivalPoint.code, departurePoint.code, returnDateTime, cabin, baggageIncluded, directFlight),
            );
          }
        } else {
          if (multiDestinationTrips && multiDestinationTrips.length > 0) {
            for (let index = 0; index < multiDestinationTrips.length; index++) {
              const multiTrip = multiDestinationTrips[index];

              const departureDateTime = multiTrip.departureTime
                ? `${moment(multiTrip.departureDate).format('YYYY-MM-DD')}T${convertTime(multiTrip.departureTime)}`
                : `${moment(multiTrip.departureDate).format('YYYY-MM-DD')}T00:00:01Z`;

              trips.push(
                new Trip(
                  multiTrip.departurePoint.code,
                  multiTrip.arrivalPoint.code,
                  departureDateTime,
                  cabin,
                  baggageIncluded,
                  directFlight,
                ),
              );
            }
          }
        }

        const {
          data: { succeeded, data, errors },
        } = await travel.post<FlightSearchRequest, AxiosResponse<Response<FlightSearchResponse>>>('AirBySector/Search', {
          trips,
          userId: travelers && travelers.length > 0 ? travelers[0].id || travelers[0].userId  : loggedUserId,
          userGroupId: travelers && travelers.length > 0 ? travelers[0].userGroupId : userGroupId,
          currencyCode: countryCurrency,
          sessionToken: newSessionToken ? newSessionToken : sessionToken,
          isBusSearch: false,
          adults: travelers && travelers.length > 0 ? travelers.length : 1,
          children: 0,
          infants: 0,
          maxResults: 0,
          billingCompanyId: branchCompany?.id,
        });

        if (!succeeded) {
          throw errors;
        }

        dispatch(flightsSearchSuccessful({ ...data, currency: countryCurrency }));
        /* dispatch(flightResultsSuccessful(data.results.airResults)); */
        resolve(data);
      } catch (ex) {
        const errors = (ex as ErrorPayload).response?.data?.Errors;

        const event: TravelErrorEvent = {
          SOURCE: 'TRAVEL_HOME',
          RESULTS: 0,
          TRAVEL_PRODUCT_TYPE: 'FLIGHTS',
          TRAVEL_FROM: departurePoint?.code ?? '',
          TRAVEL_TO: arrivalPoint?.code ?? '',
          TRAVEL_DESTINATION_TYPE: '',
          TRAVEL_TRIP_TYPE: '',
          TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), departureDate).toString(),
          TRAVEL_DATE_FROM: departureDate?.toString() ?? '',
          TRAVEL_DATE_TO: returnDate?.toString() ?? '',
          TRAVEL_TRIP_LENGTH: (daysCount(departureDate, returnDate) + 1).toString(),
          TRAVEL_PASSENGERS: '1',
          TRAVEL_ADULTS: '1',
          TRAVEL_CHILDREN: '0',
          TRAVEL_INFANTS: '0',
          TRAVEL_CLASS_OF_SERVICE: '',
          TRAVEL_ROOMS: '1',
          TRAVEL_WITH_BAGGAGE: '',
          TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
          SEARCHID: '',
          ERROR_MESSAGE: errors?.join('') ?? '',
        };
        amplitudelogEvent(amplitudeEvents.TRAVEL_SEARCH_ERROR, event, accountResult);

        if (errors?.some((e: string) => e.toLowerCase().includes('no results'))) {
          const emptyData: FlightSearchResponse = {
            results: [],
            searchId: '',
            distanceRestriction: 'Domestic',
            resultsAvailableUntil: new Date(),
            resultsCount: 0,
          };
          dispatch(flightsSearchSuccessful({ ...emptyData, currency: countryCurrency }));
          resolve(emptyData);
          return;
        }

        Sentry.addBreadcrumb({
          category: 'flight',
          message: 'Could not search flights',
          data: { ex, response: (ex as ErrorPayload).response },
        });
        Sentry.captureMessage('[Flights] Could not search flights');
        dispatch(flightsSearchError(String(ex)));
        reject(ex);
      }
    });


export const searchFlights =
  ({
    baggageIncluded,
    departurePoint,
    arrivalPoint,
    departureDate,
    travelers,
    cabin,
    returnDate,
    newSessionToken,
    departureTime,
    returnTime,
    directFlight,
    branchCompany,
    multiDestinationTrips,
    flightStretch,
  }: SearchFlightsArguments) =>
  async (dispatch: Dispatch, getState: () => RootState) =>
    new Promise(async (resolve, reject) => {
      dispatch(
        flightsSearchRequest({
          baggageIncluded,
          departurePoint,
          arrivalPoint,
          departureDate,
          cabin,
          returnDate,
          travelers,
          departureTime,
          returnTime,
          directFlight,
          branchCompany,
          multiDestinationTrips,
          flightStretch,
        }),
      );

      const { accountResult } = getState().user;
      if (!accountResult) return;

      const { sessionToken, countryCurrency } = accountResult;

      try {
        let trips: Trip[] = [];
        if (flightStretch !== FlightStretch.MULTIDESTINATION) {
          const departureDateTime = departureTime
            ? `${moment(departureDate).format('YYYY-MM-DD')}T${convertTime(departureTime)}`
            : `${moment(departureDate).format('YYYY-MM-DD')}T00:00:01Z`;
          trips = [
            new Trip(departurePoint.code, arrivalPoint.code, departureDateTime, cabin, baggageIncluded, directFlight),
          ];

          if (returnDate) {
            const returnDateTime = returnTime
              ? `${moment(returnDate).format('YYYY-MM-DD')}T${convertTime(returnTime)}`
              : `${moment(returnDate).format('YYYY-MM-DD')}T00:00:02Z`;
            trips.push(
              new Trip(arrivalPoint.code, departurePoint.code, returnDateTime, cabin, baggageIncluded, directFlight),
            );
          }
        } else {
          if (multiDestinationTrips && multiDestinationTrips.length > 0) {
            for (let index = 0; index < multiDestinationTrips.length; index++) {
              const multiTrip = multiDestinationTrips[index];

              const departureDateTime = multiTrip.departureTime
                ? `${moment(multiTrip.departureDate).format('YYYY-MM-DD')}T${convertTime(multiTrip.departureTime)}`
                : `${moment(multiTrip.departureDate).format('YYYY-MM-DD')}T00:00:01Z`;

              trips.push(
                new Trip(
                  multiTrip.departurePoint.code,
                  multiTrip.arrivalPoint.code,
                  departureDateTime,
                  cabin,
                  baggageIncluded,
                  directFlight,
                ),
              );
            }
          }
        }

        const {
          data: { succeeded, data, errors },
        } = await travel.post<FlightSearchRequest, AxiosResponse<Response<FlightSearchResponse>>>('Air/AirSearchV2', {
          trips,
          currencyCode: accountResult.countryCurrency ?? countryCurrency,
          sessionToken: newSessionToken ? newSessionToken : sessionToken,
          isBusSearch: false,
          adults: travelers && travelers.length > 0 ? travelers.length : 1,
          children: 0,
          infants: 0,
          maxResults: 0,
          billingCompanyId: branchCompany?.id,
        });

        if (!succeeded) {
          throw errors;
        }
        
        dispatch(flightsSearchSuccessful({ ...data, currency: countryCurrency }));
        resolve(data);
      } catch (ex) {        
        const errors = (ex as ErrorPayload).response?.data?.Errors;
        const isInvalidToken = (errors?.some((e: string) => e.toLowerCase().includes('invalidtoken')));
        updateAccount(dispatch, { ...accountResult, bIsSessionToken: isInvalidToken });
        const event: TravelErrorEvent = {
          SOURCE: 'TRAVEL_HOME',
          RESULTS: 0,
          TRAVEL_PRODUCT_TYPE: 'FLIGHTS',
          TRAVEL_FROM: departurePoint?.code ?? '',
          TRAVEL_TO: arrivalPoint?.code ?? '',
          TRAVEL_DESTINATION_TYPE: '',
          TRAVEL_TRIP_TYPE: '',
          TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), departureDate).toString(),
          TRAVEL_DATE_FROM: departureDate?.toString() ?? '',
          TRAVEL_DATE_TO: returnDate?.toString() ?? '',
          TRAVEL_TRIP_LENGTH: (daysCount(departureDate, returnDate) + 1).toString(),
          TRAVEL_PASSENGERS: '1',
          TRAVEL_ADULTS: '1',
          TRAVEL_CHILDREN: '0',
          TRAVEL_INFANTS: '0',
          TRAVEL_CLASS_OF_SERVICE: '',
          TRAVEL_ROOMS: '1',
          TRAVEL_WITH_BAGGAGE: '',
          TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
          SEARCHID: '',
          ERROR_MESSAGE: errors?.join('') ?? '',
        };
        amplitudelogEvent(amplitudeEvents.TRAVEL_SEARCH_ERROR, event, accountResult);

        if (errors?.some((e: string) => e.toLowerCase().includes('no results'))) {
          const emptyData: FlightSearchResponse = {
            results: [],
            searchId: '',
            distanceRestriction: 'Domestic',
            resultsAvailableUntil: new Date(),
            resultsCount: 0,
          };
          dispatch(flightsSearchSuccessful({ ...emptyData, currency: countryCurrency }));
          resolve(emptyData);
          return;
        }

        Sentry.addBreadcrumb({
          category: 'flight',
          message: 'Could not search flights',
          data: { ex, response: (ex as ErrorPayload).response },
        });
        Sentry.captureMessage('[Flights] Could not search flights');
        dispatch(flightsSearchError(String(ex)));
        reject(ex);
      }
    });

export const flightResults =
  (currentOrderCriteria: FlightResultRequestOrderCriteriaEnum, newFilters?: FlightFilter) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    try {
      const { accountResult } = getState().user;
      if (!accountResult) return;
      const { sessionToken, id: loggedUserId } = accountResult;

      const { orderCriteria, filters, search, isEnd, isLoadingResults, travelers } = getState().flights;
      if (!search) return;
      const { results, searchId, resultsCount, distanceRestriction, lowestLogicalFare } = search;

      const maxResultsPerPage = Number(ENV.SEARCH_MAX_RESULT_PER_PAGE);

      if (
        (isEnd && (newFilters === undefined || newFilters === null) && orderCriteria === currentOrderCriteria) ||
        ((newFilters === undefined || newFilters === null) &&
          results.length >= resultsCount &&
          orderCriteria === currentOrderCriteria)
      ) {
        return;
      }

      if (isLoadingResults && (newFilters === undefined || newFilters === null)) return;

      if (isLoadingResults && cancel) cancel();

      let countLowerBound = 0;
      let countUpperBound = 9;   
      
      if(!newFilters){
        countLowerBound = getState().flights.resultCountLowerBoundBySector;
        countUpperBound = getState().flights.resultCountUpperBoundBySector;
      }
      else{
        dispatch(changeResultBySectorPagination(countLowerBound, countUpperBound));
      }

      dispatch(flightResultsRequest(currentOrderCriteria, newFilters));
      const {
        data: { data },
      } = await travel.post<FlightResultRequest, AxiosResponse<Response<FlightResultResponse[]>>>(
        'Air/AirGroupedResultsv2',
        {
          userId: travelers && travelers.length > 0 ? travelers[0].id || travelers[0].userId  : loggedUserId,
          travelArrangerUserId:
            accountResult.isTravelArranger && travelers && travelers.length > 0 ? loggedUserId : undefined,
          searchId,
          resultCountLowerBound: !newFilters && orderCriteria === currentOrderCriteria ? countLowerBound : 0,
          resultCountUpperBound:
            !newFilters && orderCriteria === currentOrderCriteria
              ? countUpperBound + maxResultsPerPage
              : maxResultsPerPage,
          orderCriteria: currentOrderCriteria,
          distanceRestriction,
          lowestLogicalFare,
          sessionToken,
          orderDesc: false,
          adults: travelers && travelers.length > 0 ? travelers.length : 1,
          ...(newFilters || filters),
        } as FlightResultRequest,
        {
          cancelToken: new CancelToken((c) => {
            cancel = c;
          }),
        },
      );
      dispatch(changeResultBySectorPagination(countLowerBound + maxResultsPerPage, countUpperBound + maxResultsPerPage));
      dispatch(flightResultsSuccessful(data));
    } catch (ex) {
      Sentry.addBreadcrumb({
        category: 'flight',
        message: 'Could not get flight results',
        data: { ex, response: (ex as ErrorPayload).response },
      });
      Sentry.captureMessage('[Flights] Could not get flight results');
      dispatch(flightResultsError(String(ex)));
    }
  };

  export const flightResultsBySector =
  (currentOrderCriteria: FlightResultRequestOrderCriteriaEnum, newFilters?: FlightFilter, fromFilter?: boolean) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    try {
      console.log('flightResultsBySector')
      const { accountResult } = getState().user;
      if (!accountResult) return;
      const { sessionToken, id: loggedUserId } = accountResult;

      const { orderCriteria, filters, search, isEnd, isLoadingResults, travelers, arrivalPoint, 
        departurePoint,departureDate,baggageIncluded, cabin, directFlight,returnDate,departureTime,returnTime, flightStretch, multiDestinationTrips } = getState().flights;
      if (!search) return;
      const { results, searchId, resultsCount, distanceRestriction, lowestLogicalFare } = search;
        
      const maxResultsPerPage = Number(ENV.SEARCH_MAX_RESULT_PER_PAGE);

      if (
        (isEnd && (newFilters === undefined || newFilters === null) && orderCriteria === currentOrderCriteria) ||
        ((newFilters === undefined || newFilters === null) &&
          results.length >= resultsCount &&
          orderCriteria === currentOrderCriteria)
      ) {
        return;
      }

      if (isLoadingResults && (newFilters === undefined || newFilters === null)) return;

      if (isLoadingResults && cancel) cancel();
      dispatch(flightResultsRequest(currentOrderCriteria, newFilters));
      let trips: Trip[] = [];
      if (flightStretch !== FlightStretch.MULTIDESTINATION) {
        const departureDateTime = departureTime
          ? `${moment(departureDate).format('YYYY-MM-DD')}T${convertTime(departureTime)}`
          : `${moment(departureDate).format('YYYY-MM-DD')}T00:00:01Z`;
        trips = [
          new Trip(departurePoint.code, arrivalPoint.code, departureDateTime, cabin, baggageIncluded, directFlight),
        ];

        if (returnDate) {
          const returnDateTime = returnTime
            ? `${moment(returnDate).format('YYYY-MM-DD')}T${convertTime(returnTime)}`
            : `${moment(returnDate).format('YYYY-MM-DD')}T00:00:02Z`;
          trips.push(
            new Trip(arrivalPoint.code, departurePoint.code, returnDateTime, cabin, baggageIncluded, directFlight),
          );
        }
      } else {
        if (multiDestinationTrips && multiDestinationTrips.length > 0) {
          for (let index = 0; index < multiDestinationTrips.length; index++) {
            const multiTrip = multiDestinationTrips[index];

            const departureDateTime = multiTrip.departureTime
              ? `${moment(multiTrip.departureDate).format('YYYY-MM-DD')}T${convertTime(multiTrip.departureTime)}`
              : `${moment(multiTrip.departureDate).format('YYYY-MM-DD')}T00:00:01Z`;

            trips.push(
              new Trip(
                multiTrip.departurePoint.code,
                multiTrip.arrivalPoint.code,
                departureDateTime,
                cabin,
                baggageIncluded,
                directFlight,
              ),
            );
          }
        }
      }
      let countLowerBound = 0;
      let countUpperBound = 9;
      if(!fromFilter){
        countLowerBound = getState().flights.resultCountLowerBoundBySector;
        countUpperBound = getState().flights.resultCountUpperBoundBySector;
      }
      else{
        dispatch(changeResultBySectorPagination(countLowerBound, countUpperBound));
      }

      const {
        data: { data },
      } = await travel.post<FlightResultRequestBySector, AxiosResponse<Response<FlightResultResponse[]>>>(
        'AirBySector/Results',
        {
          userId: travelers && travelers.length > 0 ? travelers[0].id || travelers[0].userId  : loggedUserId,
          searchId,
          resultCountLowerBound: countLowerBound,
          resultCountUpperBound:countUpperBound,
          orderCriteria: currentOrderCriteria,
          distanceRestriction,
          lowestLogicalFare,
          sessionToken,
          orderDesc: false,
          adults: travelers && travelers.length > 0 ? travelers.length : 1,
          ...(newFilters || filters),
          optionSelectionIds: getState().flights.optionsIds.length === search.sectorsCount ? getState().flights.optionsIds.slice(0, getState().flights.optionsIds.length - 1) : getState().flights.optionsIds,
          trips:trips
        } as FlightResultRequest,
        {
          cancelToken: new CancelToken((c) => {
            cancel = c;
          }),
        },
      );
      if(fromFilter){
        dispatch(flightResultsBySectorSuccessfulFilter(data))
      }
      else{
        dispatch(changeResultBySectorPagination(countLowerBound+9, countUpperBound+9));
        dispatch(flightResultsBySectorSuccessful(data))
      }

    } catch (ex) {
      console.log(ex)
      Sentry.addBreadcrumb({
        category: 'flight',
        message: 'Could not get flight results',
        data: { ex, response: (ex as ErrorPayload).response },
      });
      Sentry.captureMessage('[Flights] Could not get flight results');
      dispatch(flightResultsError(String(ex)));
    }
  };

  export const selectFlightResultsBySector =
  (currentOrderCriteria: FlightResultRequestOrderCriteriaEnum, newFilters?: FlightFilter) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    try {
      const { accountResult } = getState().user;
      if (!accountResult) return;
      const { sessionToken, id: loggedUserId } = accountResult;

      const { orderCriteria, filters, search, isEnd, isLoadingResults, travelers, arrivalPoint, 
        departurePoint,departureDate,baggageIncluded, cabin, directFlight,returnDate,departureTime,returnTime, flightStretch, multiDestinationTrips } = getState().flights;
      if (!search) return;
      const { results, searchId, resultsCount, distanceRestriction, lowestLogicalFare } = search;
        
      const maxResultsPerPage = Number(ENV.SEARCH_MAX_RESULT_PER_PAGE);

      if (
        (isEnd && (newFilters === undefined || newFilters === null) && orderCriteria === currentOrderCriteria) ||
        ((newFilters === undefined || newFilters === null) &&
          results.length >= resultsCount &&
          orderCriteria === currentOrderCriteria)
      ) {
        return;
      }

      if (isLoadingResults && (newFilters === undefined || newFilters === null)) return;

      if (isLoadingResults && cancel) cancel();

      dispatch(flightResultsRequest(currentOrderCriteria, newFilters));
      let trips: Trip[] = [];
      if (flightStretch !== FlightStretch.MULTIDESTINATION) {
        const departureDateTime = departureTime
          ? `${moment(departureDate).format('YYYY-MM-DD')}T${convertTime(departureTime)}`
          : `${moment(departureDate).format('YYYY-MM-DD')}T00:00:01Z`;
        trips = [
          new Trip(departurePoint.code, arrivalPoint.code, departureDateTime, cabin, baggageIncluded, directFlight),
        ];

        if (returnDate) {
          const returnDateTime = returnTime
            ? `${moment(returnDate).format('YYYY-MM-DD')}T${convertTime(returnTime)}`
            : `${moment(returnDate).format('YYYY-MM-DD')}T00:00:02Z`;
          trips.push(
            new Trip(arrivalPoint.code, departurePoint.code, returnDateTime, cabin, baggageIncluded, directFlight),
          );
        }
      } else {
        if (multiDestinationTrips && multiDestinationTrips.length > 0) {
          for (let index = 0; index < multiDestinationTrips.length; index++) {
            const multiTrip = multiDestinationTrips[index];

            const departureDateTime = multiTrip.departureTime
              ? `${moment(multiTrip.departureDate).format('YYYY-MM-DD')}T${convertTime(multiTrip.departureTime)}`
              : `${moment(multiTrip.departureDate).format('YYYY-MM-DD')}T00:00:01Z`;

            trips.push(
              new Trip(
                multiTrip.departurePoint.code,
                multiTrip.arrivalPoint.code,
                departureDateTime,
                cabin,
                baggageIncluded,
                directFlight,
              ),
            );
          }
        }
      }
      const {
        data: { data },
      } = await travel.post<FlightResultRequestBySector, AxiosResponse<Response<FlightResultResponse[]>>>(
        'AirBySector/Results',
        {
          userId: travelers && travelers.length > 0 ? travelers[0].id || travelers[0].userId  : loggedUserId,
          searchId,
          resultCountLowerBound: 0,
          resultCountUpperBound: 10,
          orderCriteria: currentOrderCriteria,
          distanceRestriction,
          lowestLogicalFare,
          sessionToken,
          orderDesc: false,
          adults: travelers && travelers.length > 0 ? travelers.length : 1,
          ...(newFilters || filters),
          optionSelectionIds: getState().flights.optionsIds? getState().flights.optionsIds : [],
          trips:trips
        } as FlightResultRequest,
        {
          cancelToken: new CancelToken((c) => {
            cancel = c;
          }),
        },
      );
      dispatch(selectFlightBySectorSuccessful(data));
    } catch (ex) {
      console.log(ex)
      Sentry.addBreadcrumb({
        category: 'flight',
        message: 'Could not get flight results',
        data: { ex, response: (ex as ErrorPayload).response },
      });
      Sentry.captureMessage('[Flights] Could not get flight results');
      dispatch(flightResultsError(String(ex)));
    }
  };

  const convertTotalPriceToPrice = (totalPrice: TotalPrice) => ({
      taxes: totalPrice.taxes,
      taxesValue: totalPrice.taxes, // Compatibilidade para outras propriedades similares
      total: totalPrice.total,
      currencyCode: totalPrice.currencyCode,
      amount: totalPrice.amount,
      fees: totalPrice.fees,
      surcharges: totalPrice.surcharges,
      discountAmount: totalPrice.discountAmount,
      commissionAmount: totalPrice.commissionAmount,
      penaltyAmount: totalPrice.penaltyAmount,
    });

export const confirmFlightSolicitation =
  (
    flight: FlightValidationResponse,
    justifyForBreakingRules: string,
    reason: string,
    validateResponse: FlightValidateResponse,
    validationIdsBySector?: string[],
    upSellId?: string,
    upSellOptionId?: number,
    costCenterId?: string,
    breakRulesMessageId?: string,
    creditCardId?:string
  ) =>
  (dispatch: Dispatch, getState: () => RootState) =>
    new Promise<void>(async (resolve, reject) => {
      const { accountResult } = getState().user;
      const {
        departureDate,
        search,
        travelers,
        returnDate,
        departurePoint,
        arrivalPoint,
        branchCompany,
        multiDestinationTrips,
      } = getState().flights;
      try {
        const { currency } = getState().i18n.country;

        if (!currency || !search || !accountResult) return;

        const currentUserAsTraveler: Traveler = {
          id: accountResult.id,
          gender: accountResult.gender,
          firstName: accountResult.name,
          lastName: accountResult.lastName,
          phone: accountResult.phoneNumber,
          email: accountResult.email,
          nationality: accountResult.country,
          dob: new Date(accountResult.birthDate),
          documentNumber: accountResult.documentNumber,
          passportNumber: accountResult.passportNumber,
          paxType: 'Adult',
        };

        let airBookTravelers = [];

        if (travelers && travelers.length > 0) {
          airBookTravelers = travelers.map((traveler) => ({
            id: traveler.id !== '' ? traveler.id : traveler.userId,
            gender: traveler.gender,
            firstName: traveler.name,
            lastName: traveler.lastName,
            phone: traveler.phoneNumber,
            email: traveler.email,
            nationality: traveler.country,
            dob: new Date(traveler.birthDate),
            documentNumber: traveler.documentNumber,
            passportNumber: traveler.passportNumber,
            paxType: 'Adult',
          }));
        } else {
          airBookTravelers.push(currentUserAsTraveler);
        }
        
        let priceBySector = undefined;

        if (flight.totalPrice !== undefined) {
          priceBySector = convertTotalPriceToPrice(flight.totalPrice);
        }

        const transformSectors = (airValidateResponse) => {
          let sectorNumber = 1;
        
          const sectors = airValidateResponse.flatMap((tripOption) =>
            tripOption.tripOption.sectors.map((sector) => ({
              sectorNumber: sectorNumber++,
              flights: sector.flights.map((flight) => ({
                departureAirport: flight.departureAirport,
                arrivalAirport: flight.arrivalAirport,
                departureDate: new Date(flight.departureDate),
                arrivalDate: new Date(flight.arrivalDate),
                airlineCode: flight.airlineCode,
                airlineName: flight.airlineName,
                baggageRestrictions: flight.baggageRestrictions,
                handBaggage: flight.handBaggage,
                opAirLineCode: flight.opAirLineCode,
                opAirLineName: flight.opAirLineName,
                number: flight.number,
                cabin: flight.cabin,
                flightTimeInternal: flight.flightTimeInternal,
                equipment: flight.equipment,
                numberOfStops: flight.numberOfStops,
                technicalStopTime: null,
                fareBasis: flight.fareBasis,
                fareFamilies: flight.fareFamilies,
                airlineConfirmationCode: flight.airlineConfirmationCode || null,
                departureAirportName: flight.departureAirportName || '',
                departureCityName: flight.departureCityName || '',
                departureCountryName: flight.departureCountryName || '',
                departureStateName: flight.departureStateName || '',
                arrivalAirportName: flight.arrivalAirportName || '',
                arrivalCityName: flight.arrivalCityName || '',
                arrivalCountryName: flight.arrivalCountryName || '',
                arrivalStateName: flight.arrivalStateName || '',
              })),
              totalDuration: sector.totalDuration,
            }))
          );
        
          return sectors;
        };

        const firstFlight = flight?.tripOption !== undefined ? flight?.tripOption?.sectors[0].flights[0] : flight.airValidateResponse[0].tripOption.sectors[0].flights[0];
        const departureDateUTC = multiDestinationTrips === undefined ? new Date(firstFlight.departureDate) : multiDestinationTrips?.first().departureDate;
      
        const airBook: AirBook = {
          sessionToken: accountResult.sessionToken,
          searchid: search.searchId,
          optionid: flight?.tripOption?.optionId ?? 1,
          currency: accountResult.countryCurrency ?? currency,
          travelDate: departureDateUTC ?? multiDestinationTrips?.first().departureDate ?? undefined,
          distanceRestriction: search.distanceRestriction,
          sectors: flight?.tripOption?.sectors ?? transformSectors(flight.airValidateResponse),
          price: flight.tripOption !== undefined ? flight.tripOption.price : priceBySector,
          travelers: airBookTravelers,
          lowestLogicalFare: search.lowestLogicalFare,
          upSellId: upSellId && upSellId !== '' ? upSellId : undefined,
          upSellOptionId: upSellOptionId && upSellOptionId !== 0 ? upSellOptionId : undefined,
          validationIds: validationIdsBySector ?? undefined
        };
        
        const { data } = await travel.post('Travels/Create', {
          userId: travelers && travelers.length > 0 ? travelers[0].id || travelers[0].userId : accountResult.id,
          userFullName:
            travelers && travelers.length > 0
              ? `${travelers[0]?.name} ${travelers[0]?.lastName}`
              : `${accountResult.name} ${accountResult.lastName}`,
          companyId: accountResult.companyId,
          currency: accountResult.countryCurrency ?? currency,
          airBook,
          justifyForBreakingRules,
          reason,
          costCenterId,
          travelArrangerUserId:
            accountResult.isTravelArranger && travelers && travelers.length > 0 ? accountResult.id : undefined,
          validationId: validationIdsBySector === undefined ? validateResponse.validationId : undefined,
          validationIds: validationIdsBySector ?? undefined,
          documentId: validateResponse?.documentId ?? 0,
          breakRuleReasonId: breakRulesMessageId,
          billingCompanyId: branchCompany?.id,
          lowestLogicalFareList: search.lowestLogicalFareList,
          creditCardId
        } as FlightSolicitationRequest);
        dispatch(setConfirmationSuccessful(true));
        dispatch(flightsClear());
        resolve(data);
        debugger;
        dispatch(setTravelItineraryId(data.value.data.airBook.travelItineraryId));
      } catch (ex) {
        dispatch(setConfirmationError(true));
        reject(ex);
      }
    });

export const getFilters = () => async (dispatch: Dispatch, getState: () => RootState) =>
  new Promise<FlightFilterResponse>(async (resolve, reject) => {
    try {
      const { accountResult } = getState().user;
      const { search } = getState().flights;

      if (search?.searchId) {
        const {
          data: { data },
        } = await travel.get<Response<FlightFilterResponse>>('Air/AirFilter', {
          params: {
            sessionToken: accountResult?.sessionToken || '',
            searchId: search?.searchId,
          },
          ...axiosConfig,
        });
        const { airFilterTabs } = data;

        dispatch(flightsTabFilterSuccessful({ airFilterTabs }));

        resolve(data);
      }
    } catch (ex) {
      Sentry.addBreadcrumb({
        category: 'flight',
        message: 'Could not get flight filters',
        data: { ex, response: (ex as ErrorPayload).response },
      });
      Sentry.captureMessage('[Flights] Could not get flight filters');
      reject(ex);
    }
  });

  export const getFiltersBySector = () => async (dispatch: Dispatch, getState: () => RootState) =>
    new Promise<FlightFilterResponse>(async (resolve, reject) => {
      try {
        const { accountResult } = getState().user;
        const { search } = getState().flights;
  
        if (search?.searchId) {
          const optionsIds = getState().flights.optionsIds.length === search.sectorsCount ? getState().flights.optionsIds.slice(0, getState().flights.optionsIds.length - 1) : getState().flights.optionsIds;

          const {
            data: { data },
          } = await travel.post<Response<FlightFilterResponse>>('AirBySector/Filter',{
              searchId: search?.searchId,
              sessionToken: accountResult?.sessionToken || '',
              optionSelectionIds:getState().flights.optionsIds ? optionsIds : []
          }, {
            params: {
              sessionToken: accountResult?.sessionToken || '',
              searchId: search?.searchId,
            },
            ...axiosConfig,
          });
          const { airFilterTabs } = data;
    
          dispatch(flightsTabFilterSuccessful({ airFilterTabs }));
    
          resolve(data);
        }
      } catch (ex) {
        Sentry.addBreadcrumb({
          category: 'flight',
          message: 'Could not get flight filters',
          data: { ex, response: (ex as ErrorPayload).response },
        });
        Sentry.captureMessage('[Flights] Could not get flight filters');
        reject(ex);
      }
    });

export const fetchFlightDetails = async (
  searchId: string,
  sessionToken: string,
  optionId: number,
  userId: string,
  distanceRestriction: string,
  lowestLogicalFare: number,
  adults: number,
  upSellId?: string,
  upSellOptionId = 0,
) =>
  travel.post<FlightValidationRequest, AxiosResponse<Response<FlightValidationResponse>>>('Air/AirValidation', {
    searchId,
    sessionToken,
    optionId,
    upSellOptionId,
    upSellId,
    userId,
    distanceRestriction,
    lowestLogicalFare,
    adults,
  });

  export const fetchFlightDetailsBySector = async (
    searchId: string,
    sessionToken: string,
    userId: string,
    distanceRestriction: string,
    lowestLogicalFare: number,
    adults: number,
    airValidateBySector:string[],
    baggageIncluded,
    departurePoint,
    arrivalPoint,
    departureDate,
    cabin,
    returnDate,
    departureTime,
    returnTime,
    directFlight,
    multiDestinationTrips,
    flightStretch,
  ) =>{
    let trips: Trip[] = [];
        if (flightStretch !== FlightStretch.MULTIDESTINATION) {
          const departureDateTime = departureTime
            ? `${moment(departureDate).format('YYYY-MM-DD')}T${convertTime(departureTime)}`
            : `${moment(departureDate).format('YYYY-MM-DD')}T00:00:01Z`;
          trips = [
            new Trip(departurePoint.code, arrivalPoint.code, departureDateTime, cabin, baggageIncluded, directFlight),
          ];

          if (returnDate) {
            const returnDateTime = returnTime
              ? `${moment(returnDate).format('YYYY-MM-DD')}T${convertTime(returnTime)}`
              : `${moment(returnDate).format('YYYY-MM-DD')}T00:00:02Z`;
            trips.push(
              new Trip(arrivalPoint.code, departurePoint.code, returnDateTime, cabin, baggageIncluded, directFlight),
            );
          }
        } else {
          if (multiDestinationTrips && multiDestinationTrips.length > 0) {
            for (let index = 0; index < multiDestinationTrips.length; index++) {
              const multiTrip = multiDestinationTrips[index];

              const departureDateTime = multiTrip.departureTime
                ? `${moment(multiTrip.departureDate).format('YYYY-MM-DD')}T${convertTime(multiTrip.departureTime)}`
                : `${moment(multiTrip.departureDate).format('YYYY-MM-DD')}T00:00:01Z`;

              trips.push(
                new Trip(
                  multiTrip.departurePoint.code,
                  multiTrip.arrivalPoint.code,
                  departureDateTime,
                  cabin,
                  baggageIncluded,
                  directFlight,
                ),
              );
            }
          }
        }
   return travel.post<FlightValidationRequest, AxiosResponse<Response<FlightValidationResponseBySector>>>('AirBySector/Validation', {
      searchId,
      sessionToken,
      userId,
      distanceRestriction,
      lowestLogicalFare,
      adults,
      airValidateBySector,
      trips
    });
  }
    

export const getAirOptionIdFromGroupedResults = (keys: string[]) => async (_: Dispatch, getState: () => RootState) =>
  new Promise<{ optionId: number }>(async (resolve, reject) => {
    try {
      const { accountResult } = getState().user;
      const { search } = getState().flights;
      const {
        data: { data },
      } = await travel.get<Response<{ optionId: number }>>('Air/AirOptionIdFromGroupedResult', {
        params: {
          sessionToken: accountResult?.sessionToken || '',
          searchId: search?.searchId,
          flightKeys: keys.join(','),
        },
      });

      resolve(data);
    } catch (error) {
      Sentry.captureMessage(`[Flights] Could not get AirOptionId from flights: ${keys}`);
      reject(error);
    }
  });

export const getGetUpsellBySector = (optionId:number,sectorNumber:number) => async (_: Dispatch, getState: () => RootState) =>
  new Promise<{ optionId: any }>(async (resolve, reject) => {
    try {
      const { accountResult } = getState().user;
      if (!accountResult) return;
      const { sessionToken, id: loggedUserId,userGroupId } = accountResult;

      const {  search, travelers, arrivalPoint, 
        departurePoint,departureDate,baggageIncluded, cabin, directFlight,returnDate,departureTime,returnTime, flightStretch, multiDestinationTrips } = getState().flights;
      if (!search) return;
      const {  distanceRestriction, lowestLogicalFare } = search;

      let trips: Trip[] = [];
      if (flightStretch !== FlightStretch.MULTIDESTINATION) {
        const departureDateTime = departureTime
          ? `${moment(departureDate).format('YYYY-MM-DD')}T${convertTime(departureTime)}`
          : `${moment(departureDate).format('YYYY-MM-DD')}T00:00:01Z`;
        trips = [
          new Trip(departurePoint.code, arrivalPoint.code, departureDateTime, cabin, baggageIncluded, directFlight),
        ];

        if (returnDate) {
          const returnDateTime = returnTime
            ? `${moment(returnDate).format('YYYY-MM-DD')}T${convertTime(returnTime)}`
            : `${moment(returnDate).format('YYYY-MM-DD')}T00:00:02Z`;
          trips.push(
            new Trip(arrivalPoint.code, departurePoint.code, returnDateTime, cabin, baggageIncluded, directFlight),
          );
        }
      } else {
        if (multiDestinationTrips && multiDestinationTrips.length > 0) {
          for (let index = 0; index < multiDestinationTrips.length; index++) {
            const multiTrip = multiDestinationTrips[index];

            const departureDateTime = multiTrip.departureTime
              ? `${moment(multiTrip.departureDate).format('YYYY-MM-DD')}T${convertTime(multiTrip.departureTime)}`
              : `${moment(multiTrip.departureDate).format('YYYY-MM-DD')}T00:00:01Z`;

            trips.push(
              new Trip(
                multiTrip.departurePoint.code,
                multiTrip.arrivalPoint.code,
                departureDateTime,
                cabin,
                baggageIncluded,
                directFlight,
              ),
            );
          }
        }
      } 

      const upsellBySectorRequest: UpsellBySectorRequest = {
        trips: trips,
        searchId: search.searchId,
        sessionToken: sessionToken,
        userId: loggedUserId,
        userGroupId: travelers && travelers.length > 0 ? travelers[0].userGroupId : userGroupId,
        adults: travelers && travelers.length > 0 ? travelers.length : 1,
        lowestLogicalFare: lowestLogicalFare,
        distanceRestriction: distanceRestriction,
        optionId: optionId,
        sectorNumber: sectorNumber,
      }

      const {
        data: { data },
      } = await travel.post<Response<{ optionId: any }>>('AirBySector/Upsell', upsellBySectorRequest,{
        params: {
          token: accountResult?.sessionToken || '',
          companyId: accountResult.companyId,
          country_code: localStorage.getItem('country_code'),
        }
      })

      resolve(data);
    } catch (error) {

      reject(error);
    }
  });

export const validateFlight = (
  flight: FlightValidationResponse,
  justifyForBreakingRules: string,
  reason: string,
  redux: RootState,
  upSellId?: string,
  upSellOptionId?: number,
  costCenterId?: string,
  creditCardId?:string,
  validationIds?: string[],
  optionIds?: number[]
) =>
  new Promise<any>(async (resolve, reject) => {
    const { accountResult } = redux.user;
    const { departureDate, search, travelers, branchCompany, multiDestinationTrips } = redux.flights;

    if (!accountResult || !search) {
      return;
    }

    try {
      
      const { currency } = redux.i18n.country;

      if (!currency || !search || !accountResult) return;

      const currentUserAsTraveler: Traveler = {
        id: accountResult.id,
        gender: accountResult.gender,
        firstName: accountResult.name,
        lastName: accountResult.lastName,
        phone: accountResult.phoneNumber,
        email: accountResult.email,
        nationality: accountResult.country,
        dob: new Date(accountResult.birthDate),
        documentNumber: accountResult.documentNumber,
        passportNumber: accountResult.passportNumber,
        paxType: 'Adult',
      };

      let airBookTravelers = [];

      if (travelers && travelers.length > 0) {
        airBookTravelers = travelers.map((traveler) => ({
          gender: traveler.gender,
          firstName: traveler.name,
          lastName: traveler.lastName,
          phone: traveler.phoneNumber,
          email: traveler.email,
          nationality: traveler.country,
          dob: new Date(traveler.birthDate),
          documentNumber: traveler.documentNumber,
          passportNumber: traveler.passportNumber,
          paxType: 'Adult',
        }));
      } else {
        airBookTravelers.push(currentUserAsTraveler);
      }

      let priceBySector = undefined;

        if (flight.totalPrice !== undefined) {
          priceBySector = convertTotalPriceToPrice(flight.totalPrice);
        }

        const transformSectors = (airValidateResponse) => {
          let sectorNumber = 1;
        
          const sectors = airValidateResponse.flatMap((tripOption) =>
            tripOption.tripOption.sectors.map((sector) => ({
              sectorNumber: sectorNumber++,
              flights: sector.flights.map((flight) => ({
                departureAirport: flight.departureAirport,
                arrivalAirport: flight.arrivalAirport,
                departureDate: new Date(flight.departureDate),
                arrivalDate: new Date(flight.arrivalDate),
                airlineCode: flight.airlineCode,
                airlineName: flight.airlineName,
                baggageRestrictions: flight.baggageRestrictions,
                handBaggage: flight.handBaggage,
                opAirLineCode: flight.opAirLineCode,
                opAirLineName: flight.opAirLineName,
                number: flight.number,
                cabin: flight.cabin,
                flightTimeInternal: flight.flightTimeInternal,
                equipment: flight.equipment,
                numberOfStops: flight.numberOfStops,
                technicalStopTime: null,
                fareBasis: flight.fareBasis,
                fareFamilies: flight.fareFamilies,
                airlineConfirmationCode: flight.airlineConfirmationCode || null,
                departureAirportName: flight.departureAirportName || '',
                departureCityName: flight.departureCityName || '',
                departureCountryName: flight.departureCountryName || '',
                departureStateName: flight.departureStateName || '',
                arrivalAirportName: flight.arrivalAirportName || '',
                arrivalCityName: flight.arrivalCityName || '',
                arrivalCountryName: flight.arrivalCountryName || '',
                arrivalStateName: flight.arrivalStateName || '',
              })),
              totalDuration: sector.totalDuration,
            }))
          );
        
          return sectors;
        };

      const firstFlight = flight?.tripOption !== undefined ? flight?.tripOption?.sectors[0].flights[0] : flight.airValidateResponse[0].tripOption.sectors[0].flights[0];
      const departureDateUTC = multiDestinationTrips === undefined ? new Date(firstFlight.departureDate) : multiDestinationTrips?.first().departureDate;

      const airBook: AirBook = {
        sessionToken: accountResult.sessionToken,
        searchid: search.searchId,
        optionid: flight?.tripOption?.optionId ?? 1,
        currency: accountResult.countryCurrency ?? currency,
        travelDate: departureDateUTC ?? undefined,
        distanceRestriction: search.distanceRestriction,
        sectors: flight?.tripOption?.sectors ?? transformSectors(flight.airValidateResponse),
        price: flight.tripOption !== undefined ? flight.tripOption.price : priceBySector,
        travelers: airBookTravelers,
        lowestLogicalFare: search.lowestLogicalFare,
        upSellId: upSellId && upSellId !== '' ? upSellId : undefined,
        upSellOptionId: upSellOptionId && upSellOptionId !== 0 ? upSellOptionId : undefined,
        validationIds: validationIds, // bysector
        optionIds: optionIds //bysector
      };

      const data = await travel
        .post<FlightValidationResponse>('Travels/Validate', {
          userId: travelers && travelers.length > 0 ? travelers[0].id || travelers[0].userId : accountResult.id,
          userFullName:
            travelers && travelers.length > 0
              ? `${travelers[0]?.name} ${travelers[0]?.lastName}`
              : `${accountResult.name} ${accountResult.lastName}`,
          companyId: accountResult.companyId,
          currency: accountResult.countryCurrency ?? currency,
          airBook,
          justifyForBreakingRules,
          reason,
          costCenterId,
          travelArrangerUserId:
            accountResult.isTravelArranger && travelers && travelers.length > 0 ? accountResult.id : undefined,
          billingCompanyId: branchCompany?.id,
          lowestLogicalFareList: search.lowestLogicalFareList,
          creditCardId
        } as FlightSolicitationRequest)
        .then((response) => response.data);

      resolve(data);
    } catch (ex) {
      reject(ex);
    }
  });

type SearchExchangeArguments = {
  departureCode?: string;
  departureDate?: Date;
  arrivalCode?: string;
  arrivalDate?: Date;
  changeDeparture?: boolean;
  changeArrival?: boolean;
  newSessionToken?: string;
  originalFlight?: TravelRequested;
};

export const searchExchangeFlights =
  ({
    departureCode,
    departureDate,
    arrivalCode,
    arrivalDate,
    changeDeparture,
    changeArrival,
    newSessionToken,
    originalFlight,
  }: SearchExchangeArguments) =>
  async (dispatch: Dispatch, getState: () => RootState) =>
    new Promise(async (resolve, reject) => {
      const { accountResult } = getState().user;
      if (!accountResult) return;
      const { sessionToken } = accountResult;

      dispatch(
        flightsExchangeRequest({
          departureCode,
          departureDate,
          arrivalCode,
          arrivalDate,
          originalFlight,
        }),
      );

      try {
        const trips: ExchangeTrip[] = [
          {
            departurePoint: departureCode,
            arrivalPoint: arrivalCode,
            sectorNumber: 1,
            departureDate: `${moment(departureDate).format('YYYY-MM-DD')}`,
            sectorChange: changeDeparture,
          } as ExchangeTrip,
        ];

        if (arrivalDate) {
          const arrivalDateTime = `${moment(arrivalDate).format('YYYY-MM-DD')}T00:00:02Z`;
          trips.push({
            departurePoint: arrivalCode,
            arrivalPoint: departureCode,
            departureDateWithOutFormat: arrivalDateTime,
            sectorNumber: 2,
            departureDate: `${moment(arrivalDate).format('YYYY-MM-DD')}`,
            sectorChange: changeArrival,
            reservationIndex: 0,
          } as ExchangeTrip);
        }
        const { changeFlight } = getState().flights;
        const { originalFlight } = changeFlight;

        const payload: FlightExchangeRequest = {
          trips,
          currencyCode: originalFlight?.currencyCode || 'COP',
          isBusSearch: false,
          adults: 1,
          children: 0,
          infants: 0,
          maxResults: 0,
          billingCompanyId: originalFlight?.billingCompanyId || '',
          travelId: originalFlight?.id || '',
          sessionToken: newSessionToken ? newSessionToken : sessionToken,
          userId: originalFlight?.userId || '',
        };
        const {
          data: { succeeded, data, errors },
        } = await travel.post<FlightExchangeRequest, AxiosResponse<Response<FlightExchangeResponse>>>(
          'Air/AirSearchExchange',
          payload,
          {
            cancelToken: new CancelToken((c) => {
              cancel = c;
            }),
          },
        );

        if (!succeeded) {
          dispatch(flightsExchangeError(String(errors)));
          reject(errors);
        }

        dispatch(flightsExchangeSuccessful({ ...data, results: [], resultsCount: data.results.length }));
        resolve({ ...data, results: [], resultsCount: data.results.length });
      } catch (ex) {
        const errors = (ex as ErrorPayload)?.response?.data?.Errors;

        /*  const event: TravelErrorEvent = {
        SOURCE: 'TRAVEL_HOME',
        RESULTS: 0,
        TRAVEL_PRODUCT_TYPE: 'FLIGHTS',
        TRAVEL_FROM: departurePoint?.code ?? '',
        TRAVEL_TO: arrivalPoint?.code ?? '',
        TRAVEL_DESTINATION_TYPE: '',
        TRAVEL_TRIP_TYPE: '',
        TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), departureDate).toString(),
        TRAVEL_DATE_FROM: departureDate?.toString() ?? '',
        TRAVEL_DATE_TO: returnDate?.toString() ?? '',
        TRAVEL_TRIP_LENGTH: (daysCount(departureDate, returnDate) + 1).toString(),
        TRAVEL_PASSENGERS: '1',
        TRAVEL_ADULTS: '1',
        TRAVEL_CHILDREN: '0',
        TRAVEL_INFANTS: '0',
        TRAVEL_CLASS_OF_SERVICE: '',
        TRAVEL_ROOMS: '1',
        TRAVEL_WITH_BAGGAGE: '',
        TRAVEL_BRANCH_CODE: process.env.REACT_APP_RAPPI_BRANCH_CODE ?? '',
        SEARCHID: '',
        ERROR_MESSAGE: errors?.join(''),
      };
      amplitudelogEvent(amplitudeEvents.TRAVEL_SEARCH_ERROR, event, accountResult); */

        if (errors?.some((e: string) => e.toLowerCase().includes('no results'))) {
          const emptyData: FlightExchangeResponse = {
            results: [],
            searchId: '',
            distanceRestriction: 'Domestic',
            resultsAvailableUntil: new Date(),
            resultsCount: 0,
          };
          dispatch(flightsExchangeSuccessful(emptyData));
          resolve(emptyData);
          return;
        }

        /* Sentry.addBreadcrumb({
        category: 'flight',
        message: 'Could not search flights',
        data: { ex, response: ex.response },
      });
      Sentry.captureMessage('[Flights] Could not search flights'); */
        dispatch(flightsExchangeError(String(ex)));
        reject(ex);
      }
    });

export const confirmFlightExchange =
  (
    sectors: Sector[],
    price: Price,
    optionId: number,
    traveler: AdminUser,
    justifyForBreakingRules: string,
    reason: string,
    breakRulesMessageId?: string,
    creditCardId?:string
  ) =>
  (dispatch: Dispatch, getState: () => RootState) =>
    new Promise<void>(async (resolve, reject) => {
      const { accountResult } = getState().user;
      const { changeFlight, changeFlightSearch: search } = getState().flights;
      const { originalFlight, departureDate, departureCode, arrivalCode, arrivalDate } = changeFlight;

      if (
        !search ||
        !changeFlight ||
        !accountResult ||
        !originalFlight ||
        !departureCode ||
        !departureDate ||
        !arrivalCode ||
        !arrivalDate
      )
        return;

      try {
        const airBookTraveler: Traveler = {
          id: traveler.id,
          gender: traveler.gender,
          firstName: traveler.name,
          lastName: traveler.lastName,
          phone: traveler.phoneNumber,
          email: traveler.email ?? '',
          nationality: traveler.country ?? '',
          dob: new Date(traveler.birthDate),
          documentNumber: traveler.documentNumber,
          passportNumber: traveler.passportNumber,
          paxType: 'Adult',
        };

        const airBook: AirBook = {
          sessionToken: accountResult.sessionToken,
          searchid: search.searchId,
          optionid: optionId,
          currency: originalFlight.currencyCode,
          travelDate: departureDate,
          distanceRestriction: search.distanceRestriction,
          sectors,
          price,
          travelers: [airBookTraveler],
          lowestLogicalFare: search.lowestLogicalFare,
          upSellId: undefined,
          upSellOptionId: 0,
        };

        const payload: FlightSolicitationRequest = {
          userId: originalFlight.userId ?? '',
          userFullName: `${traveler.name} ${traveler.lastName}`,
          currency: originalFlight.currencyCode,
          companyId: originalFlight.companyId || '',
          airBook,
          justifyForBreakingRules,
          reason,
          costCenterId: originalFlight.costCenterId ?? '',
          travelArrangerUserId:
            accountResult.isTravelArranger && traveler && traveler.id !== accountResult.id
              ? accountResult.id
              : undefined,
          billingCompanyId: originalFlight.billingCompanyId ?? '',
          lowestLogicalFareList: search.lowestLogicalFareList ?? [],
          breakRuleReasonId: breakRulesMessageId ?? '',
          TravelIdFlightExchanged: originalFlight.id,
          creditCardId
        };
        const { data } = await travel.post('Travels/Create', payload);

        dispatch(flightsExchangeClear());

        resolve(data);
        debugger;
        dispatch(setTravelItineraryId(data.value.data.airBook.travelItineraryId));
      } catch (ex) {
        /* const event: TravelErrorEvent = {
        SOURCE: 'TRAVEL_HOME',
        RESULTS: 0,
        TRAVEL_PRODUCT_TYPE: 'FLIGHTS',
        TRAVEL_FROM: departurePoint?.code ?? '',
        TRAVEL_TO: arrivalPoint?.code ?? '',
        TRAVEL_DESTINATION_TYPE: search?.distanceRestriction ?? '',
        TRAVEL_TRIP_TYPE: !(departurePoint && !returnDate) ? 'true' : 'false',
        TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), departureDate).toString(),
        TRAVEL_DATE_FROM: departureDate?.toString() ?? '',
        TRAVEL_DATE_TO: returnDate?.toString() ?? '',
        TRAVEL_TRIP_LENGTH: (daysCount(departureDate, returnDate) + 1).toString(),
        TRAVEL_PASSENGERS: '1',
        TRAVEL_ADULTS: '1',
        TRAVEL_CHILDREN: '0',
        TRAVEL_INFANTS: '0',
        TRAVEL_CLASS_OF_SERVICE: '',
        TRAVEL_ROOMS: '1',
        TRAVEL_WITH_BAGGAGE: '',
        TRAVEL_BRANCH_CODE: process.env.REACT_APP_RAPPI_BRANCH_CODE ?? '',
        SEARCHID: search?.searchId ?? '',
        ERROR_MESSAGE: ex.toString(),
      };
      await amplitudelogEvent(amplitudeEvents.TRAVEL_BOOKING_ERROR, event, accountResult); */

        reject(ex);
      }
    });

export const flightExchangeResults =
  (currentOrderCriteria: FlightResultRequestOrderCriteriaEnum, newFilters?: FlightFilter) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    try {
      const { accountResult } = getState().user;
      if (!accountResult) return;
      const { sessionToken } = accountResult;

      const { changeFlightStatus, changeFlight } = getState().flights;
      const { originalFlight } = changeFlight;
      const { orderCriteria, filters, isEnd, isLoadingResults } = changeFlightStatus;
      const { changeFlightSearch: search } = getState().flights;

      if (!search) return;
      const { results, searchId, resultsCount, distanceRestriction, lowestLogicalFare } = search;

      const maxResultsPerPage = Number(ENV.API.REACT_APP_SEARCH_MAX_RESULT_PER_PAGE);

      if (
        (isEnd && (newFilters === undefined || newFilters === null) && orderCriteria === currentOrderCriteria) ||
        ((newFilters === undefined || newFilters === null) &&
          results.length >= resultsCount &&
          orderCriteria === currentOrderCriteria)
      ) {
        return;
      }

      if (isLoadingResults && (newFilters === undefined || newFilters === null)) return;

      if (isLoadingResults && cancel) cancel();

      const payload: FlightResultRequest = {
        userId: originalFlight?.userId || '',
        searchId,
        resultCountLowerBound:0,
        resultCountUpperBound:
          50,
        orderCriteria: currentOrderCriteria,
        distanceRestriction: distanceRestriction || 'Domestic',
        lowestLogicalFare: lowestLogicalFare || 0,
        sessionToken,
        orderDesc: false,
        ...(newFilters || filters),
      };
      dispatch(flightsExchangeResultsRequest(currentOrderCriteria, newFilters));
      const {
        data: { data },
      } = await travel.post<FlightResultRequest, AxiosResponse<Response<FlightResultResponse[]>>>(
        'Air/AirResults',
        payload,
        {
          cancelToken: new CancelToken((c) => {
            cancel = c;
          }),
        },
      );

      dispatch(flightsExchangeResultsSuccessful(data));
    } catch (ex) {
      /* Sentry.addBreadcrumb({
      category: 'flight',
      message: 'Could not get flight results',
      data: { ex, response: (ex as ErrorPayload).response },
    });
    Sentry.captureMessage('[Flights] Could not get flight results'); */
      dispatch(flightsExchangeResultsError(String(ex)));
    }
  };
  export const getFlightExchangeResults =
  (currentOrderCriteria: FlightResultRequestOrderCriteriaEnum, newFilters?: FlightFilter) =>
  async (dispatch: Dispatch, getState: () => RootState) =>
    new Promise<any>(async (resolve, reject) => {
    try {
      const { accountResult } = getState().user;
      if (!accountResult) return;
      const { sessionToken } = accountResult;

      const { changeFlightStatus, changeFlight } = getState().flights;
      const { originalFlight } = changeFlight;
      const { orderCriteria, filters, isEnd, isLoadingResults } = changeFlightStatus;
      const { changeFlightSearch: search } = getState().flights;

      if (!search) return;
      const { results, searchId, resultsCount, distanceRestriction, lowestLogicalFare } = search;

      const maxResultsPerPage = Number(ENV.API.REACT_APP_SEARCH_MAX_RESULT_PER_PAGE);

      if (
        (isEnd && (newFilters === undefined || newFilters === null) && orderCriteria === currentOrderCriteria) ||
        ((newFilters === undefined || newFilters === null) &&
          results.length >= resultsCount &&
          orderCriteria === currentOrderCriteria)
      ) {
        return;
      }

      if (isLoadingResults && (newFilters === undefined || newFilters === null)) return;

      if (isLoadingResults && cancel) cancel();

      const payload: FlightResultRequest = {
        userId: originalFlight?.userId || '',
        searchId,
        resultCountLowerBound: !newFilters && orderCriteria === currentOrderCriteria ? results.length : 0,
        resultCountUpperBound:
          !newFilters && orderCriteria === currentOrderCriteria
            ? results.length + maxResultsPerPage
            : maxResultsPerPage,
        orderCriteria: currentOrderCriteria,
        distanceRestriction: distanceRestriction || 'Domestic',
        lowestLogicalFare: lowestLogicalFare || 0,
        sessionToken,
        orderDesc: false,
        ...(newFilters || filters),
      };
      //dispatch(flightsExchangeResultsRequest(currentOrderCriteria, newFilters));
      const {
        data: { data },
      } = await travel.post<FlightResultRequest, AxiosResponse<Response<FlightResultResponse[]>>>(
        'Air/AirResults',
        payload,
        {
          cancelToken: new CancelToken((c) => {
            cancel = c;
          }),
        },
      );

      return resolve(data);
    } catch (ex) {
      /* Sentry.addBreadcrumb({
      category: 'flight',
      message: 'Could not get flight results',
      data: { ex, response: (ex as ErrorPayload).response },
    });
    Sentry.captureMessage('[Flights] Could not get flight results'); */
      //dispatch(flightsExchangeResultsError(String(ex)));
    }
  });
export const validateExchangeFlight = (
  traveler: AdminUser,
  sectors: Sector[],
  price: Price,
  optionId: number,
  justifyForBreakingRules: string,
  reason: string,
  redux: RootState,
  creditCardId?: string
) =>
  new Promise<any>(async (resolve, reject) => {
    const { accountResult } = redux.user;
    const { changeFlight, changeFlightSearch: search } = redux.flights;
    const { departureDate, arrivalDate, departureCode, arrivalCode, originalFlight } = changeFlight ?? {
      departureDate: undefined,
      arrivalDate: undefined,
      departureCode: '',
      arrivalCode: '',
      originalFlight: undefined,
    };

    if (originalFlight === undefined || originalFlight.userId === undefined) return;
    if (!accountResult || !search) {
      return;
    }
    if (departureDate === undefined || arrivalDate === undefined || departureCode === '' || arrivalCode === '') return;
    const currency = changeFlight.originalFlight?.currencyCode;

    try {
      const airBookTraveler: Traveler = {
        id: traveler.id,
        gender: traveler.gender,
        firstName: traveler.name,
        lastName: traveler.lastName,
        phone: traveler.phoneNumber,
        email: traveler.email || '',
        nationality: traveler.country || '',
        dob: new Date(traveler.birthDate),
        documentNumber: traveler.documentNumber,
        passportNumber: traveler.passportNumber,
        paxType: 'Adult',
      };

      const airBook: AirBook = {
        sessionToken: accountResult.sessionToken,
        searchid: search.searchId,
        optionid: optionId,
        currency: currency ?? 'COP',
        travelDate: departureDate,
        distanceRestriction: search.distanceRestriction,
        sectors,
        price,
        travelers: [airBookTraveler],
        lowestLogicalFare: search.lowestLogicalFare,
        upSellId: undefined,
        upSellOptionId: 0,
      };

      const payload: FlightSolicitationRequest = {
        userId: originalFlight.userId,
        userFullName: `${traveler.name} ${traveler.lastName}`,
        companyId: traveler.companyId ?? '',
        currency: currency ?? 'COP',
        airBook,
        justifyForBreakingRules,
        reason,
        costCenterId: originalFlight.costCenterId ?? '',
        travelArrangerUserId:
          accountResult.isTravelArranger && traveler && traveler.id !== accountResult.id ? accountResult.id : undefined,
        billingCompanyId: originalFlight.billingCompanyId ?? '',
        lowestLogicalFareList: search.lowestLogicalFareList ?? [],
        creditCardId
      };

      const data = await travel
        .post<FlightValidationResponse>('Travels/Validate', payload)
        .then((response) => response.data);

      resolve(data);
    } catch (ex) {
      //   const event: TravelErrorEvent = {
      //     SOURCE: 'TRAVEL_HOME',
      //     RESULTS: 0,
      //     TRAVEL_PRODUCT_TYPE: 'FLIGHTS',
      //     TRAVEL_FROM: departurePoint?.code ?? '',
      //     TRAVEL_TO: arrivalPoint?.code ?? '',
      //     TRAVEL_DESTINATION_TYPE: search?.distanceRestriction ?? '',
      //     TRAVEL_TRIP_TYPE: !(departurePoint && !returnDate) ? 'true' : 'false',
      //     TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), departureDate).toString(),
      //     TRAVEL_DATE_FROM: departureDate?.toString() ?? '',
      //     TRAVEL_DATE_TO: returnDate?.toString() ?? '',
      //     TRAVEL_TRIP_LENGTH: (daysCount(departureDate, returnDate) + 1).toString(),
      //     TRAVEL_PASSENGERS: '1',
      //     TRAVEL_ADULTS: '1',
      //     TRAVEL_CHILDREN: '0',
      //     TRAVEL_INFANTS: '0',
      //     TRAVEL_CLASS_OF_SERVICE: '',
      //     TRAVEL_ROOMS: '1',
      //     TRAVEL_WITH_BAGGAGE: '',
      //     TRAVEL_BRANCH_CODE: process.env.REACT_APP_RAPPI_BRANCH_CODE ?? '',
      //     SEARCHID: search?.searchId ?? '',
      //     ERROR_MESSAGE: ex.toString(),
      //   };
      //   await amplitudelogEvent(amplitudeEvents.TRAVEL_BOOKING_ERROR, event, accountResult);

      reject(ex);
    }
  });

